<template>
  <StandardCard
    @action-clicked="createComment"
    :actions="cardActions"
    :border="false"
    :card-test-id="editorSectionTestId"
    :divided="false"
    :title="title"
    card-title-class="fs-18"
  >
    <RichTextEditor
      ref="richTextEditorRef"
      :height="200"
      placeholder="Type message here"
    />
  </StandardCard>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import RichTextEditor from '@/shared/components/RichTextEditor.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api: Api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const store = useStore();

const props = defineProps({
  commentType: {
    type: String,
    default: null,
  },
  modelId: {
    type: String,
    default: null,
  },
  modelType: {
    type: String,
    default: null,
  },
  parentCommentId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['created']);

const richTextEditorRef = ref(null);
const currentMemberId = ref(store.state.profile.id);

const COMMENT_TYPE_LABELS = {
  comment: {
    cardTitle: 'Post comment',
    actionTitle: 'Post comment',
  },
  reply: {
    cardTitle: 'Reply to comment',
    actionTitle: 'Post reply',
  },
};

const labelsForType = computed(() => COMMENT_TYPE_LABELS[props.commentType]);

const editorSectionTestId = computed(() =>
  props.commentType === 'reply' ? 'new-reply-editor' : 'new-comment-editor',
);

const title = computed(() => labelsForType.value?.cardTitle ?? '');

const cardActions = computed(() => [
  {
    title: labelsForType.value?.actionTitle ?? '',
    event: 'create',
    role: 'create',
  },
]);

const courseCohortId = computed(() => {
  return route.params.cohortId || route.query.cohortId;
});

function isRichTextEmpty(html) {
  if (!html?.trim()) return true;
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').trim() === '';
}

async function createComment() {
  const editor = richTextEditorRef.value;
  const html = editor?.getSemanticHTML() ?? '';

  if (isRichTextEmpty(html)) {
    eventBus.chime('Comment requires text');
    return;
  }

  const params = {
    model_type: props.modelType,
    model_id: props.modelId,
    author_id: currentMemberId.value,
    parent_comment_id: props.parentCommentId,
    text: html,
    cohort_id: courseCohortId.value,
  };

  const response = await Api.comment.create(params);
  if (response.status === 200) {
    eventBus.chime(`Posted ${props.commentType}`);
    editor?.setText('');
    emit('created');
  } else {
    eventBus.chime('An error occurred');
  }
}
</script>
